import { Alert, Button, Card, Skeleton, Stack } from '@chakra-ui/react';
import { toaster, useColorModeValue } from '@mono/chakra';
import { isHttpError, useHttp, useHttpQuery } from '@mono/http-hook';
import { FC, useMemo, useState } from 'react';
import AddCollaboratorsModal from './components/AddCollaboratorsModal';
import CollaboratorsTable from './components/CollaboratorsTable';
import RemoveCollaboratorDialog from './components/RemoveCollaboratorDialog';
import {
  Collaborator,
  CollaboratorsResponse,
  ProjectRoleValue,
  getCollaboratorKey,
} from './types';
import { useCurrentProject } from '../projects/hooks';

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const ProjectCollaboratorsTab: FC = () => {
  const http = useHttp();
  const projectQuery = useCurrentProject();
  const project = projectQuery.data?.project;

  const [pendingRoleChanges, setPendingRoleChanges] = useState<Set<string>>(
    new Set()
  );
  const [isAddOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Collaborator | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const collaboratorsQuery = useHttpQuery<CollaboratorsResponse>({
    queryKey: ['project-collaborators', project?.id],
    config: {
      method: 'get',
      url: project
        ? `/api/v1/organizations/${project.organizationId}/projects/${project.id}/collaborators`
        : '',
      withCredentials: true,
    },
    options: {
      enabled: Boolean(project),
    },
  });

  const collaborators = collaboratorsQuery.data?.collaborators ?? [];

  const canManage = useMemo(() => {
    if (!project) return false;
    return project.role === 'ADMIN' || project.role === 'MAINTAIN';
  }, [project]);

  const collaboratorUserIds = useMemo(() => {
    const ids = new Set<string>();
    collaborators.forEach((collaborator) => {
      if (collaborator.user?.id) {
        ids.add(collaborator.user.id);
      }
    });
    return ids;
  }, [collaborators]);

  const collaboratorTeamIds = useMemo(() => {
    const ids = new Set<string>();
    collaborators.forEach((collaborator) => {
      if (collaborator.team?.id) {
        ids.add(collaborator.team.id);
      }
    });
    return ids;
  }, [collaborators]);

  const handleRoleChange = async (
    collaborator: Collaborator,
    role: ProjectRoleValue
  ) => {
    if (!project) return;

    const key = getCollaboratorKey(collaborator);
    setPendingRoleChanges((prev) => new Set(prev).add(key));

    try {
      const payload = collaborator.user
        ? { userId: collaborator.user.id, role }
        : { teamId: collaborator.team?.id ?? '', role };

      await http.post(
        `/api/v1/organizations/${project.organizationId}/projects/${project.id}/collaborators`,
        payload,
        {
          withCredentials: true,
        }
      );

      await collaboratorsQuery.refetch();
      toaster.create({
        type: 'success',
        title: 'Permissions updated',
        description: 'Collaborator access level updated successfully.',
      });
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to update collaborator permissions right now.'
      );
      toaster.create({
        type: 'error',
        title: 'Failed to update permissions',
        description: message,
      });
    } finally {
      setPendingRoleChanges((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleRemoveCollaborator = async () => {
    if (!project || !removeTarget) return;

    setIsRemoving(true);

    try {
      await http.delete(
        `/api/v1/organizations/${project.organizationId}/projects/${project.id}/collaborators/${removeTarget.id}`,
        {
          withCredentials: true,
        }
      );

      await collaboratorsQuery.refetch();
      await projectQuery.refetch();
      toaster.create({
        type: 'success',
        title: 'Collaborator removed',
        description: 'Access removed successfully.',
      });
      setRemoveTarget(null);
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to remove collaborator right now.'
      );
      toaster.create({
        type: 'error',
        title: 'Failed to remove collaborator',
        description: message,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  if (!project) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Project not available</Alert.Title>
          <Alert.Description>
            We couldn’t find this project. Please return to the project list.
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    );
  }

  const collaboratorRoleLabel = (role: ProjectRoleValue) =>
    role.charAt(0) + role.slice(1).toLowerCase();

  const cardBg = useColorModeValue('bg.surface', 'gray.900');
  const projectRoleLabel = collaboratorRoleLabel(
    (project.role ?? 'READ') as ProjectRoleValue
  );

  return (
    <Stack gap="6">
      {!canManage ? (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Read-only access</Alert.Title>
            <Alert.Description>
              You have {projectRoleLabel.toLowerCase()} permissions and cannot
              manage collaborators.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      <Card.Root bg={cardBg}>
        <Card.Header
          gap="3"
          display="flex"
          justifyContent="space-between"
          flexWrap="wrap"
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={'row'}
        >
          <Stack gap="1">
            <Card.Title>Collaborators</Card.Title>
            <Card.Description>
              People and teams currently collaborating on this project.
            </Card.Description>
          </Stack>
          {canManage ? (
            <Button
              colorPalette="primary"
              size="sm"
              onClick={() => setAddOpen(true)}
            >
              Add collaborators
            </Button>
          ) : null}
        </Card.Header>
        <Card.Body gap="4">
          {collaboratorsQuery.isLoading ? (
            <Stack gap="3">
              <Skeleton height="44px" borderRadius="md" />
              <Skeleton height="44px" borderRadius="md" />
              <Skeleton height="44px" borderRadius="md" />
            </Stack>
          ) : collaborators.length === 0 ? (
            <Alert.Root status="info" variant="subtle">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>No collaborators yet</Alert.Title>
                <Alert.Description>
                  Add teammates or entire teams to kick off collaboration.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : (
            <CollaboratorsTable
              collaborators={collaborators}
              canManage={canManage}
              pendingRoleChanges={pendingRoleChanges}
              onRoleChange={handleRoleChange}
              onRemove={(collaborator) => setRemoveTarget(collaborator)}
            />
          )}
        </Card.Body>
      </Card.Root>

      <AddCollaboratorsModal
        isOpen={isAddOpen}
        onClose={() => setAddOpen(false)}
        project={project}
        collaboratorUserIds={collaboratorUserIds}
        collaboratorTeamIds={collaboratorTeamIds}
        onAdded={async () => {
          await collaboratorsQuery.refetch();
          await projectQuery.refetch();
        }}
      />

      <RemoveCollaboratorDialog
        collaborator={removeTarget}
        isRemoving={isRemoving}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => void handleRemoveCollaborator()}
      />
    </Stack>
  );
};

export default ProjectCollaboratorsTab;
